import { useState, useCallback } from 'react';

import { isNotNull } from 'utils/tsHelpers';
import { useListOptions } from './useListOptions';
import { useTranslation } from '../../../../hooks/useTranslation';
import { useQueryStateContext } from '../../../../contexts';
import { useVisualComponentsContext } from '../../../../contexts/visualComponents';
import { ValidatedSingleStringValueInputProps } from '../types';
import { DEFAULT_TYPEAHEAD_QUERY_LIMIT } from '../../../../constants';
import { useCombineFetchedOptions } from 'modules/queryBuilder/renderers/hooks/useCombineFetchedOptions';
import { ClientDataType } from 'modules/queryBuilder/types/clientDataType';
import { LIST_MEMBERSHIP_SINGLE, USER_LIST_MEMBERSHIP_SINGLE } from 'utils/fieldConstants';

export const SingleListSelect = ({
  onChange,
  value,
  isInvalid,
  isOpenByDefault,
  hasWarning,
  dataType,
  field,
  fieldType,
  comparator,
}: ValidatedSingleStringValueInputProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const { ConditionValueSingleStringSelectWithButton: VisualComponent } =
    useVisualComponentsContext();
  const { isReadOnly } = useQueryStateContext();
  const [optionsPage, setOptionsPage] = useState(0);
  const isFieldUserList = field === USER_LIST_MEMBERSHIP_SINGLE || field === LIST_MEMBERSHIP_SINGLE;
  const isDataTypeSubscribeEvent =
    dataType === ClientDataType.Subscribe || dataType === ClientDataType.UnSubscribe;
  const isUserListSubscribeEventField = isFieldUserList && isDataTypeSubscribeEvent;
  const { options, didError, isLoading, listsCount } = useListOptions({
    search,
    page: optionsPage,
    values: [value].filter(isNotNull),
    excludeDynamicLists: isUserListSubscribeEventField,
  });

  const isDisabled = isReadOnly || isLoading || didError;
  const hasError = didError || !!isInvalid;
  const link = value ? `/segmentation?emailListId=${value}` : undefined;

  const { combinedOptions, resetCombinedOptions } = useCombineFetchedOptions({
    options,
    page: optionsPage,
    isLoading,
  });

  const handleSetSearch = (newSearch: string) => {
    setSearch(newSearch);
    setOptionsPage(0);
    resetCombinedOptions();
  };

  const onFetch = useCallback(() => {
    // Don't fetch if still loading previous results
    if (!isLoading) {
      setOptionsPage(optionsPage + 1);
    }
  }, [optionsPage, isLoading]);

  return (
    <VisualComponent
      isDisabled={isDisabled}
      isLoading={isLoading}
      options={combinedOptions}
      hasError={hasError}
      hasWarning={hasWarning}
      isOpenByDefault={isOpenByDefault}
      onChange={onChange}
      value={value}
      dataType={dataType}
      field={field}
      fieldType={fieldType}
      comparator={comparator}
      placeholder={t('v2.conditionBuilder.singleListPlaceholder')}
      search={search}
      setSearch={handleSetSearch}
      link={link}
      tooltipContent={t('v2.conditionBuilder.singleListOpenTooltip')}
      isVirtualized
      canFetch={isLoading || listsCount > (optionsPage + 1) * DEFAULT_TYPEAHEAD_QUERY_LIMIT.limit}
      onFetch={onFetch}
    />
  );
};
